import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api, type ApiEnvelope } from '@/services/api';
import { useNotify } from '@/app/providers/NotifyProvider';

/** GET helper returning the `data` field. */
export function useApiData<T>(key: unknown[], url: string | null, opts: Partial<UseQueryOptions<ApiEnvelope<T>>> = {}) {
  return useQuery<ApiEnvelope<T>>({
    queryKey: key,
    queryFn: () => api.get<T>(url as string),
    enabled: url !== null,
    ...opts,
  });
}

/**
 * Mutation helper. Every successful write invalidates all cached queries — the original
 * kept one in-memory dataset, so any change was visible everywhere immediately.
 */
export function useApiMutation<TVars, TRes = unknown>(fn: (v: TVars) => Promise<ApiEnvelope<TRes>>, opts: { silent?: boolean } = {}) {
  const qc = useQueryClient();
  const notify = useNotify();
  return useMutation<ApiEnvelope<TRes>, Error, TVars>({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries(),
    onError: (e) => {
      if (!opts.silent) notify.error(e);
    },
  });
}
